// This list is manually maintained until our Contentful plan supports localization
// in all necessary locales, after which we can query this list using GraphQL
export enum LocaleCode {
  AR = "ar",
  AR_EG = "ar-EG",
  BG_BG = "bg-BG",
  BN = "bn",
  CS_CZ = "cs-CZ",
  DA_DK = "da-DK",
  DE = "de",
  EN_US = "en-US",
  EL_GR = "el-GR",
  ES = "es",
  ES_MX = "es-MX",
  ET_EE = "et-EE",
  FI_FI = "fi-FI",
  FR = "fr",
  GA_IE = "ga-IE",
  HA_NG = "ha-NG",
  HE = "he",
  HI = "hi",
  HR_HR = "hr-HR",
  HU = "hu",
  ID_ID = "id-ID",
  IT = "it",
  JA = "ja",
  KO_KR = "ko-KR",
  LT = "lt",
  LV_LV = "lv-LV",
  MS = "ms",
  MT_MT = "mt-MT",
  NL_NL = "nl-NL",
  PL = "pl",
  PT_BR = "pt-BR",
  RO = "ro",
  RU = "ru",
  SK_SK = "sk-SK",
  SL_SI = "sl-SI",
  SV = "sv",
  SW_KE = "sw-KE",
  TA = "ta",
  TH = "th",
  TL = "tl",
  TR_TR = "tr-TR",
  UK_UA = "uk-UA",
  UR_PK = "ur-PK",
  VI = "vi",
  ZH_CN = "zh-CN",
  ZH_TW = "zh-TW",
}

// This tracks which languages each article is available in. It is manually maintained
// until our Contentful plan supports localization in all necessary locales, after which
// we can query this list using GraphQL. This approach is quite fragile since the slug
// must be manually kept consistent across translations of the same article in Contentful,
// but this is the easiest identifier to use.
export const enterpriseEndUserArticleSlugToSupportedLanguagesMap: {
  [key: string]: LocaleCode[]
} = {
  "get-verified": [
    LocaleCode.AR,
    LocaleCode.BG_BG,
    LocaleCode.BN,
    LocaleCode.CS_CZ,
    LocaleCode.DA_DK,
    LocaleCode.DE,
    LocaleCode.EL_GR,
    LocaleCode.EN_US,
    LocaleCode.ES,
    LocaleCode.ET_EE,
    LocaleCode.FI_FI,
    LocaleCode.FR,
    LocaleCode.GA_IE,
    LocaleCode.HA_NG,
    LocaleCode.HR_HR,
    LocaleCode.HU,
    LocaleCode.ID_ID,
    LocaleCode.IT,
    LocaleCode.LV_LV,
    LocaleCode.LT,
    LocaleCode.MS,
    LocaleCode.MT_MT,
    LocaleCode.NL_NL,
    LocaleCode.PL,
    LocaleCode.PT_BR,
    LocaleCode.RO,
    LocaleCode.SK_SK,
    LocaleCode.SL_SI,
    LocaleCode.SV,
    LocaleCode.SW_KE,
    LocaleCode.TL,
  ],
  "account-recovery": [
    LocaleCode.AR_EG,
    LocaleCode.BN,
    LocaleCode.DE,
    LocaleCode.ES_MX,
    LocaleCode.EN_US,
    LocaleCode.FR,
    LocaleCode.HA_NG,
    LocaleCode.HE,
    LocaleCode.HI,
    LocaleCode.HU,
    LocaleCode.ID_ID,
    LocaleCode.IT,
    LocaleCode.JA,
    LocaleCode.KO_KR,
    LocaleCode.LT,
    LocaleCode.MS,
    LocaleCode.NL_NL,
    LocaleCode.PL,
    LocaleCode.PT_BR,
    LocaleCode.RO,
    LocaleCode.RU,
    LocaleCode.SV,
    LocaleCode.TA,
    LocaleCode.TH,
    LocaleCode.TR_TR,
    LocaleCode.UK_UA,
    LocaleCode.VI,
    LocaleCode.UR_PK,
    LocaleCode.ZH_CN,
    LocaleCode.ZH_TW,
  ],
}

export const localeCodeToLanguageNameMap: { [key in LocaleCode]: string } = {
  [LocaleCode.AR]: "Arabic",
  [LocaleCode.AR_EG]: "Arabic (Egypt)",
  [LocaleCode.BG_BG]: "Bulgarian (Bulgaria)",
  [LocaleCode.BN]: "Bengali",
  [LocaleCode.CS_CZ]: "Czech (Czech Republic)",
  [LocaleCode.DA_DK]: "Danish (Denmark)",
  [LocaleCode.DE]: "German",
  [LocaleCode.EL_GR]: "Greek (Greece)",
  [LocaleCode.EN_US]: "English (US)",
  [LocaleCode.ES]: "Spanish",
  [LocaleCode.ES_MX]: "Spanish (Mexico)",
  [LocaleCode.ET_EE]: "Estonian (Estonia)",
  [LocaleCode.FI_FI]: "Finnish (Finland)",
  [LocaleCode.FR]: "French",
  [LocaleCode.GA_IE]: "Irish (Ireland)",
  [LocaleCode.HA_NG]: "Hausa (Nigeria)",
  [LocaleCode.HE]: "Hebrew",
  [LocaleCode.HI]: "Hindi",
  [LocaleCode.HR_HR]: "Croatian (Croatia)",
  [LocaleCode.HU]: "Hungarian",
  [LocaleCode.ID_ID]: "Indonesian (Indonesia)",
  [LocaleCode.IT]: "Italian",
  [LocaleCode.JA]: "Japanese",
  [LocaleCode.KO_KR]: "Korean",
  [LocaleCode.LT]: "Lithuanian",
  [LocaleCode.LV_LV]: "Latvian (Latvia)",
  [LocaleCode.MS]: "Malay",
  [LocaleCode.MT_MT]: "Maltese (Malta)",
  [LocaleCode.NL_NL]: "Dutch (Netherlands)",
  [LocaleCode.PL]: "Polish",
  [LocaleCode.PT_BR]: "Portuguese (Brazil)",
  [LocaleCode.RO]: "Romanian",
  [LocaleCode.RU]: "Russian",
  [LocaleCode.SK_SK]: "Slovak (Slovakia)",
  [LocaleCode.SL_SI]: "Slovenian (Slovenia)",
  [LocaleCode.SW_KE]: "Swahili (Kenya)",
  [LocaleCode.SV]: "Swedish",
  [LocaleCode.TA]: "Tamil",
  [LocaleCode.TH]: "Thai",
  [LocaleCode.TR_TR]: "Turkish",
  [LocaleCode.TL]: "Tagalog",
  [LocaleCode.UR_PK]: "Urdu (Pakistan)",
  [LocaleCode.UK_UA]: "Ukrainian",
  [LocaleCode.VI]: "Vietnamese",
  [LocaleCode.ZH_CN]: "Chinese (Simplified)",
  [LocaleCode.ZH_TW]: "Chinese (Traditional)",
}
